import { prefersReducedMotion } from '../accessibility.js';

/**
 * Custom scene cursor: a quiet pulsing circle that tightens over the book to
 * show the page can be grabbed. Only mounted for fine pointers — on touch
 * there is nothing to follow, and hiding the native cursor there would be
 * meaningless.
 *
 * Purely decorative: `pointer-events: none`, and the native cursor is hidden
 * only inside the scene so controls elsewhere on the page behave normally.
 */

const FOLLOW = 0.28;

export function createSceneCursor({ scene }) {
  const element = document.createElement('div');
  element.className = 'scene__cursor';
  element.setAttribute('aria-hidden', 'true');
  scene.append(element);
  scene.dataset.cursor = 'custom';

  const reducedMotion = prefersReducedMotion();

  let targetX = 0;
  let targetY = 0;
  let x = 0;
  let y = 0;
  let scale = 1;
  let targetScale = 1;
  let visible = false;
  let frame = null;
  let destroyed = false;

  function render() {
    element.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) scale(${scale.toFixed(3)})`;
  }

  function step() {
    if (destroyed) return;

    x += (targetX - x) * FOLLOW;
    y += (targetY - y) * FOLLOW;
    scale += (targetScale - scale) * FOLLOW;
    render();

    const settled =
      Math.abs(targetX - x) < 0.1 && Math.abs(targetY - y) < 0.1 && Math.abs(targetScale - scale) < 0.002;

    frame = settled ? null : window.requestAnimationFrame(step);
  }

  function wake() {
    if (destroyed || frame !== null) return;
    frame = window.requestAnimationFrame(step);
  }

  function onPointerMove(event) {
    if (event.pointerType !== 'mouse') return;
    targetX = event.clientX;
    targetY = event.clientY;

    if (!visible) {
      visible = true;
      x = targetX;
      y = targetY;
      element.classList.add('is-visible');
      render();
    }

    if (reducedMotion) {
      x = targetX;
      y = targetY;
      scale = targetScale;
      render();
      return;
    }

    wake();
  }

  function onPointerLeave() {
    visible = false;
    element.classList.remove('is-visible');
  }

  scene.addEventListener('pointermove', onPointerMove);
  scene.addEventListener('pointerleave', onPointerLeave);

  return {
    /** @param {{ overPage: boolean, grabbing: boolean }} state */
    setState({ overPage, grabbing }) {
      element.classList.toggle('is-over-page', overPage && !grabbing);
      element.classList.toggle('is-grabbing', grabbing);
      targetScale = grabbing ? 0.72 : overPage ? 0.86 : 1;
      if (reducedMotion) {
        scale = targetScale;
        render();
        return;
      }
      wake();
    },

    destroy() {
      destroyed = true;
      if (frame !== null) window.cancelAnimationFrame(frame);
      scene.removeEventListener('pointermove', onPointerMove);
      scene.removeEventListener('pointerleave', onPointerLeave);
      delete scene.dataset.cursor;
      element.remove();
    }
  };
}
